import { format, formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { describeRevision } from '@/lib/blog/history-model';
import type { BlogRevisionSummary } from '@/lib/blog/types';

interface RevisionListProps {
  revisions: BlogRevisionSummary[];
  currentVersion: number;
  publishedVersion: number | null;
  onRestore: (version: number) => void;
  onSelect: (version: number) => void;
}

/**
 * The history of one copy, newest first.
 *
 * Q23: relative time is what a reader wants at a glance and an absolute date is
 * what they need to reason about a release, so both are on screen — "6 days
 * ago" is meaningless next to a deploy log without the date beside it.
 */
export function RevisionList({
  revisions,
  currentVersion,
  publishedVersion,
  onRestore,
  onSelect,
}: RevisionListProps) {
  if (revisions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="revision-list-empty">
        This copy has no revisions yet.
      </p>
    );
  }

  return (
    <ul className="divide-y rounded-md border" data-testid="revision-list">
      {revisions.map((revision) => {
        const display = describeRevision(revision, { currentVersion, publishedVersion });
        const when = new Date(revision.createdAt);
        return (
          <li key={revision.version} className="flex flex-wrap items-center gap-3 p-3">
            <button
              type="button"
              className="text-left font-mono text-sm underline-offset-4 hover:underline"
              onClick={() => { onSelect(revision.version); }}
            >
              {display.label}
            </button>
            <Badge variant="outline">{display.originLabel}</Badge>
            {display.isCurrent && <Badge variant="info">current</Badge>}
            {display.isPublished && <Badge variant="success">published</Badge>}
            <span className="text-sm text-muted-foreground">{display.author}</span>
            <span className="text-sm text-muted-foreground">
              {formatDistanceToNow(when, { addSuffix: true })}
              <span className="ml-2">{format(when, 'd MMM yyyy, HH:mm')}</span>
            </span>
            {!display.isCurrent && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => { onRestore(revision.version); }}
              >
                Restore
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
